import { QueryErrorMessages } from '../enum/cube-error-message.enum';
import {
  CubeTimeDimensionGranularity,
  Weekdays,
} from '../enum/cube-schema.enum';
import { ICubeRepository } from '../interface/cube-repository.interface';
import {
  IWorkingSessionsAnalyticsActivityEnum,
  IWorkingSessionsAnalyticsActivityScoresEnum,
} from '../enum/working-sessions-analytics-activity.enum';
import { IAllScoresRepository } from '../interface/scores/scores-repository.interface';
import { AllScoresMapper } from '../mappers/scores/scores.mapper';
import { DurationAvgScoresMapper } from '../mappers/scores/duration-avg-scores.mapper';
import { getDayOfWeek } from '../helpers/datetime.helper';
import { PercentageAvgScoresMapper } from '../mappers/scores/percentage-avg-scores.mapper';
import { mapScoreTimeSeriesCube } from '../mappers/scores/score-time-series.mapper';
import { ILogManager } from '../interface/log.manager.interface';
import { LogManager } from '../helpers/log/log.manager';
import { CoreScoreNames } from '../enum/score-name-cube.enum';
import { QueryAllScoresRequestParamsDto } from '../dto/scores/request/query-all-scores-request.dto';
import { QueryMaxScoresRequestParamsDto } from '../dto/scores/request/query-user-max-scores-request.dto';
import { QueryAverageScoresRequestParamsDto } from '../dto/scores/request/query-user-average-scores-request.dto';
import { QueryPerWeekdayAverageScoresRequestParamsDto } from '../dto/scores/request/query-per-weekday-average-scores-request.dto';
import { UserAllScoresResult } from '../dto/scores/cube/mapped/user-all-scores-results.dto';
import {
  UserPercentageAvgScoresResult,
  UserPercentageScoresResult,
} from '../dto/scores/cube/mapped/user-percentage-scores.results.dto';
import { ScoreTimeSeriesResult } from '../dto/scores/cube/mapped/score-time-series-results.dto';
import {
  UserDurationAvgScoresResult,
  UserDurationScoresResult,
} from '../dto/scores/cube/mapped/user-duration-scores-results.dto';
import { QueryScoreTimeSeriesRequestParamsDto } from '../dto/scores/request/query-score-time-series-request.dto';
import { CubeConfigDto } from '../dto/common/request/cube-config.dto';

export class AllScoresRepository implements IAllScoresRepository {
  cubeRepository: ICubeRepository;
  logManager: ILogManager;
  constructor(deps: { repository: ICubeRepository; debug: boolean }) {
    this.cubeRepository = deps.repository;
    this.logManager = new LogManager({ debug: deps.debug });
  }

  async queryUserAllScores(
    params: QueryAllScoresRequestParamsDto,
    config: CubeConfigDto
  ): Promise<UserAllScoresResult[]> {
    try {
      this.logManager.debug('queryUserAllScores called with', params);
      const response = await this.cubeRepository.query(config.token, {
        measures: [
          IWorkingSessionsAnalyticsActivityEnum.FocusPercentage,
          IWorkingSessionsAnalyticsActivityEnum.DigitalIntensity,
          IWorkingSessionsAnalyticsActivityEnum.TotalSessionDuration,
          IWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration,
          IWorkingSessionsAnalyticsActivityEnum.TotalSessionCallDuration,
        ],
        timeDimensions: [
          {
            dimension: IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
            granularity: params.granularity
              ? params.granularity
              : CubeTimeDimensionGranularity.Day,
            dateRange: [params.startDate, params.endDate],
          },
        ],
        dimensions: [IWorkingSessionsAnalyticsActivityEnum.UserId],
      });
      this.logManager.debug('queryUserAllScores response', response);
      return response.map((row) => {
        return AllScoresMapper.persistenceToDomain(row);
      });
    } catch (error) {
      this.logManager.debug('Caught error in queryUserAllScores', error);
      throw new Error(`${QueryErrorMessages.QueryAllScoresError}: ${error}`);
    }
  }

  async queryUserMaxScores(
    params: QueryMaxScoresRequestParamsDto,
    config: CubeConfigDto
  ): Promise<UserAllScoresResult[]> {
    try {
      this.logManager.debug('queryUserMaxScores called with', params);
      let allTimeMaxScores = {
        'WorkingSessionsAnalyticsActivity.focusPercentage': '',
        'WorkingSessionsAnalyticsActivity.digitalIntensity': '',
        'WorkingSessionsAnalyticsActivity.totalDuration': '',
        'WorkingSessionsAnalyticsActivity.totalActivityDuration': '',
        'WorkingSessionsAnalyticsActivity.totalSessionCallDuration': '',
      };
      for (const score in IWorkingSessionsAnalyticsActivityScoresEnum) {
        const startTime = new Date().getTime();
        const response = await this.cubeRepository.query(config.token, {
          measures: [IWorkingSessionsAnalyticsActivityScoresEnum[score]],
          timeDimensions: [
            {
              dimension:
                IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
              granularity: params.granularity
                ? params.granularity
                : CubeTimeDimensionGranularity.Day,
            },
          ],
          limit: 1,
          order: [[IWorkingSessionsAnalyticsActivityScoresEnum[score], 'desc']],
        });
        const endTime1 = new Date().getTime();
        this.logManager.debug(
          'allTimeMaxScores' +
            IWorkingSessionsAnalyticsActivityScoresEnum[score] +
            'time',
          (endTime1 - startTime) / 1000
        );
        if (!response.length) {
          continue;
        }
        allTimeMaxScores[IWorkingSessionsAnalyticsActivityScoresEnum[score]] =
          response[0][IWorkingSessionsAnalyticsActivityScoresEnum[score]];
        this.logManager.debug('allTimeMaxScores response', allTimeMaxScores);
      }
      return [allTimeMaxScores].map((row) =>
        AllScoresMapper.persistenceToDomain(row)
      );
    } catch (error) {
      this.logManager.debug('Caught error in queryUserMaxScores', error);
      throw new Error(`${QueryErrorMessages.QueryUserMaxScoreError}: ${error}`);
    }
  }

  async queryUserAvgScores(
    params: QueryAverageScoresRequestParamsDto,
    config: CubeConfigDto
  ): Promise<UserAllScoresResult> {
    try {
      let endDate = new Date(params.endDate);
      endDate.setDate(endDate.getDate() + 1);
      const nextDate = endDate.toISOString().split('T')[0];

      this.logManager.debug('queryUserAvgScores called with', params);

      let calculatedDurationResponse = {};
      let calculatedPercentageResponse = {};

      if (
        !params.score ||
        [
          CoreScoreNames.Screentime,
          CoreScoreNames['Digital Working hours'],
          CoreScoreNames['Time in Calls'],
        ].includes(params.score)
      ) {
        const durationResponse = await this.cubeRepository.query(config.token, {
          measures: [
            IWorkingSessionsAnalyticsActivityEnum.TotalSessionDuration,
            IWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration,
            IWorkingSessionsAnalyticsActivityEnum.TotalSessionCallDuration,
            IWorkingSessionsAnalyticsActivityEnum.DayCount,
            IWorkingSessionsAnalyticsActivityEnum.WeekCount,
          ],
          filters: [
            {
              member: IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
              operator: 'beforeDate',
              values: [nextDate],
            },
          ],
        });
        this.logManager.debug('durationResponse', durationResponse);
        let mappedDurationResponse = durationResponse.map((row) =>
          DurationAvgScoresMapper.persistenceToDomain(row)
        );
        if (!mappedDurationResponse.length) {
          mappedDurationResponse = [{} as UserDurationAvgScoresResult];
        }
        let granularity =
          params.granularity || CubeTimeDimensionGranularity.Day;
        if (granularity == CubeTimeDimensionGranularity.Day) {
          calculatedDurationResponse = {
            screenTime:
              mappedDurationResponse[0].totalScreenTime /
              mappedDurationResponse[0].dayCount,
            workingHours:
              mappedDurationResponse[0].totalWorkingHours /
              mappedDurationResponse[0].dayCount,
            timeInCalls:
              mappedDurationResponse[0].totalTimeInCalls /
              mappedDurationResponse[0].dayCount,
          };
        } else {
          calculatedDurationResponse = {
            screenTime:
              mappedDurationResponse[0].totalScreenTime /
              mappedDurationResponse[0].weekCount,
            workingHours:
              mappedDurationResponse[0].totalWorkingHours /
              mappedDurationResponse[0].weekCount,
            timeInCalls:
              mappedDurationResponse[0].totalTimeInCalls /
              mappedDurationResponse[0].weekCount,
          };
        }
        this.logManager.debug(
          'calculatedDurationResponse',
          calculatedDurationResponse
        );
      }
      if (
        !params.score ||
        [CoreScoreNames.Focus, CoreScoreNames['Digital Intensity']].includes(
          params.score
        )
      ) {
        const percentageResponse = await this.cubeRepository.query(
          config.token,
          {
            measures: [
              IWorkingSessionsAnalyticsActivityEnum.TotalSessionDuration,
              IWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration,
              IWorkingSessionsAnalyticsActivityEnum.TotalFocusDuration,
            ],
            filters: [
              {
                member: IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
                operator: 'beforeDate',
                values: [nextDate],
              },
            ],
          }
        );
        const endTime2 = new Date().getTime();
        this.logManager.debug('percentageResponse', percentageResponse);
        let mappedPercentageResponse = percentageResponse.map((row) =>
          PercentageAvgScoresMapper.persistenceToDomain(row)
        );
        if (!mappedPercentageResponse.length) {
          mappedPercentageResponse = [{} as UserPercentageAvgScoresResult];
        }
        calculatedPercentageResponse = {
          focus:
            (100 * mappedPercentageResponse[0].totalFocusDuration) /
            mappedPercentageResponse[0].totalWorkingHours,
          digitalIntensity:
            (100 * mappedPercentageResponse[0].totalScreenTime) /
            mappedPercentageResponse[0].totalWorkingHours,
        };
        this.logManager.debug(
          'calculatedPercentageResponse',
          calculatedPercentageResponse
        );
      }
      return { ...calculatedPercentageResponse, ...calculatedDurationResponse };
    } catch (error) {
      this.logManager.debug('Caught error in queryUserAvgScores', error);
      throw new Error(
        `${QueryErrorMessages.QueryUserAvgScoresError}: ${error}`
      );
    }
  }

  async queryPerWeekdayAverageAllScores(
    params: QueryPerWeekdayAverageScoresRequestParamsDto,
    config: CubeConfigDto
  ): Promise<UserAllScoresResult> {
    try {
      let currentWeekdayString;
      if (params.weekday) {
        currentWeekdayString = params.weekday;
      } else {
        const weekday = new Date(params.endDate);
        currentWeekdayString = getDayOfWeek(weekday.getUTCDay());
      }

      let endDate = new Date(params.endDate);
      endDate.setDate(endDate.getDate() + 1);
      const nextDate = endDate.toISOString().split('T')[0];

      this.logManager.debug(
        'queryPerWeekdayAverageAllScores called with',
        params
      );
      const durationResponse = await this.cubeRepository.query(config.token, {
        measures: [
          IWorkingSessionsAnalyticsActivityEnum.TotalSessionDuration,
          IWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration,
          IWorkingSessionsAnalyticsActivityEnum.TotalSessionCallDuration,
          IWorkingSessionsAnalyticsActivityEnum.DayCount,
          IWorkingSessionsAnalyticsActivityEnum.WeekCount,
        ],
        filters: [
          {
            member: IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
            operator: 'beforeDate',
            values: [nextDate],
          },
        ],
        segments: [`WorkingSessionsAnalyticsActivity.${currentWeekdayString}`],
      });
      this.logManager.debug('durationResponse', durationResponse);
      let mappedDurationResponse = durationResponse.map((row) =>
        DurationAvgScoresMapper.persistenceToDomain(row)
      );
      if (!mappedDurationResponse.length) {
        mappedDurationResponse = [{} as UserDurationAvgScoresResult];
      }
      let calculatedDurationResponse: UserDurationScoresResult;

      calculatedDurationResponse = {
        screenTime:
          mappedDurationResponse[0].totalScreenTime /
          mappedDurationResponse[0].weekCount,
        workingHours:
          mappedDurationResponse[0].totalWorkingHours /
          mappedDurationResponse[0].weekCount,
        timeInCalls:
          mappedDurationResponse[0].totalTimeInCalls /
          mappedDurationResponse[0].weekCount,
      };
      this.logManager.debug(
        'calculatedDurationResponse',
        calculatedDurationResponse
      );
      const percentageResponse = await this.cubeRepository.query(config.token, {
        measures: [
          IWorkingSessionsAnalyticsActivityEnum.TotalSessionDuration,
          IWorkingSessionsAnalyticsActivityEnum.TotalActivityDuration,
          IWorkingSessionsAnalyticsActivityEnum.TotalFocusDuration,
        ],
        filters: [
          {
            member: IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
            operator: 'beforeDate',
            values: [nextDate],
          },
        ],
        segments: [`WorkingSessionsAnalyticsActivity.${currentWeekdayString}`],
      });
      const endTime2 = new Date().getTime();
      this.logManager.debug('percentageResponse', percentageResponse);
      let mappedPercentageResponse = percentageResponse.map((row) =>
        PercentageAvgScoresMapper.persistenceToDomain(row)
      );
      if (!mappedPercentageResponse.length) {
        mappedPercentageResponse = [{} as UserPercentageAvgScoresResult];
      }
      const calculatedPercentageResponse: UserPercentageScoresResult = {
        focus:
          (100 * mappedPercentageResponse[0].totalFocusDuration) /
          mappedPercentageResponse[0].totalWorkingHours,
        digitalIntensity:
          (100 * mappedPercentageResponse[0].totalScreenTime) /
          mappedPercentageResponse[0].totalWorkingHours,
      };
      this.logManager.debug(
        'calculatedPercentageResponse',
        calculatedPercentageResponse
      );
      return { ...calculatedPercentageResponse, ...calculatedDurationResponse };
    } catch (error) {
      this.logManager.debug(
        'Caught error in queryPerWeekdayAverageAllScores',
        error
      );
      throw new Error(
        `${QueryErrorMessages.QueryPerWeekdayAverageAllScoresError}: ${error}`
      );
    }
  }

  async queryScoreTimeSeries(
    params: QueryScoreTimeSeriesRequestParamsDto,
    config: CubeConfigDto
  ): Promise<ScoreTimeSeriesResult[]> {
    try {
      this.logManager.debug('queryScoreTimeSeries called with', params);
      let response;
      if (params.weekday) {
        response = await this.cubeRepository.query(config.token, {
          measures: [params.score],
          timeDimensions: [
            {
              dimension:
                IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
              granularity: params.granularity
                ? params.granularity
                : CubeTimeDimensionGranularity.Day,
              dateRange: [params.startDate, params.endDate],
            },
          ],
          segments: [`WorkingSessionsAnalyticsActivity.${params.weekday}`],
        });
      } else {
        response = await this.cubeRepository.query(config.token, {
          measures: [params.score],
          timeDimensions: [
            {
              dimension:
                IWorkingSessionsAnalyticsActivityEnum.LocalSessionStart,
              granularity: params.granularity
                ? params.granularity
                : CubeTimeDimensionGranularity.Day,
              dateRange: [params.startDate, params.endDate],
            },
          ],
        });
      }
      this.logManager.debug('queryScoreTimeSeries response', response);
      return response.map((row) => mapScoreTimeSeriesCube(row, params.score));
    } catch (error) {
      this.logManager.debug('Caught error in queryScoreTimeSeries', error);
      throw new Error(`${QueryErrorMessages.QueryTimeSeriesError}: ${error}`);
    }
  }
}
